import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Bell, Download, KeyRound, Mail, Save, UserPlus } from 'lucide-react';
import { admin } from '../utils/api';
import { useAuth } from '../utils/AuthContext';
import SupportAccessHistory from '../components/SupportAccessHistory';

const settings = [
  { key: 'signup', label: 'New account alerts', description: 'Email me when a user assigned to me creates an account.', icon: UserPlus },
  { key: 'export', label: 'Flyer export alerts', description: 'Email me when one of my users downloads, emails, or prepares a flyer to share.', icon: Download },
  { key: 'passwordReset', label: 'Password reset alerts', description: 'Email me when one of my users requests a password reset.', icon: KeyRound },
  { key: 'monthlyReport', label: 'Monthly activity report', description: 'Email me a monthly summary for my assigned users, including activity and popular content.', icon: Mail }
];

const AdminSettings = () => {
  const { user } = useAuth();
  const [preferences, setPreferences] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    admin.getNotificationPreferences()
      .then(response => setPreferences(response.data.preferences))
      .catch(error => {
        console.error('Settings error:', error);
        toast.error('Failed to load notification settings');
      });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const response = await admin.updateNotificationPreferences(preferences);
      setPreferences(response.data.preferences);
      toast.success('Notification settings saved');
    } catch (error) {
      toast.error('Failed to save notification settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="animate-fade-in max-w-3xl">
      <div className="mb-8"><h1 className="font-display text-3xl font-bold text-gray-900">Admin Settings</h1><p className="mt-2 text-gray-600">Choose which activity alerts and reports you receive</p></div>
      <section className="card overflow-hidden">
        <div className="flex items-center gap-3 border-b border-gray-100 p-6"><div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-100 text-primary-600"><Bell size={22} /></div><div><h2 className="font-semibold text-gray-900">Email notifications</h2><p className="text-sm text-gray-500">These apply only to users assigned to you.</p></div></div>
        {!preferences ? <div className="flex h-44 items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-500/30 border-t-primary-500" /></div> : (
          <div className="divide-y divide-gray-100">{settings.map(setting => {
            const Icon = setting.icon;
            return (
              <label key={setting.key} className="flex cursor-pointer items-center gap-4 p-6 hover:bg-gray-50">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-600"><Icon size={20} /></div>
                <div className="min-w-0 flex-1"><p className="font-medium text-gray-800">{setting.label}</p><p className="mt-0.5 text-sm text-gray-500">{setting.description}</p></div>
                <input type="checkbox" checked={Boolean(preferences[setting.key])} onChange={event => setPreferences(current => ({ ...current, [setting.key]: event.target.checked }))} className="h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
              </label>
            );
          })}</div>
        )}
      </section>
      <div className="mt-5 flex justify-end"><button type="button" onClick={save} disabled={!preferences || saving} className="btn-primary inline-flex items-center gap-2"><Save size={18} /> {saving ? 'Saving...' : 'Save Settings'}</button></div>
      {user?.canViewAs && <SupportAccessHistory />}
    </div>
  );
};

export default AdminSettings;
